import { useId } from 'react';
import { cn } from '@/lib/cn';

/**
 * The eight-pointed star lattice (DESIGN.md → Texture): small tiles, faint,
 * and ONLY in the end corner of a hero surface — bottom-left in Arabic,
 * bottom-right in English — fading out toward the content, never behind text.
 *
 * The fade is a mask anchored to the end corner, so the same element mirrors
 * with the reading direction rather than needing a second copy.
 */
export function StarLattice({ className = '', tile = 28 }: { className?: string; tile?: number }) {
  const id = useId().replace(/:/g, '');
  const half = tile / 2;
  const box = tile / 2;
  return (
    <svg
      aria-hidden="true"
      data-testid="star-lattice"
      className={cn(
        'pointer-events-none absolute bottom-0 end-0 h-44 w-56 text-lime',
        '[mask-image:radial-gradient(circle_at_100%_100%,black,transparent_72%)]',
        'rtl:[mask-image:radial-gradient(circle_at_0%_100%,black,transparent_72%)]',
        className,
      )}
    >
      <defs>
        <pattern id={`lattice-${id}`} width={tile} height={tile} patternUnits="userSpaceOnUse">
          <g fill="none" stroke="currentColor" strokeWidth="0.9">
            <rect x={half - box / 2} y={half - box / 2} width={box} height={box} />
            <rect
              x={half - box / 2}
              y={half - box / 2}
              width={box}
              height={box}
              transform={`rotate(45 ${half} ${half})`}
            />
            <circle cx={half} cy={half} r={tile / 10} />
          </g>
        </pattern>
      </defs>
      {/* ≤ 0.16, per DESIGN.md: texture, not decoration. */}
      <rect width="100%" height="100%" fill={`url(#lattice-${id})`} opacity="0.16" />
    </svg>
  );
}
